/**
 * Withdrawal Verified Page
 * 
 * Displayed after a user clicks the email verification link.
 * Shows success/error message based on verification result.
 */

import React, { useEffect, useState } from 'react';

interface WithdrawalVerifiedPageProps {
  onNavigate?: (page: string) => void;
}

const WithdrawalVerifiedPage: React.FC<WithdrawalVerifiedPageProps> = () => {
  const [success, setSuccess] = useState<boolean | null>(null);
  const [error, setError] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [wallet, setWallet] = useState<string>('');

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('success') === 'true';
    setSuccess(isSuccess);
    setError(decodeURIComponent(params.get('error') || ''));
    setAmount(params.get('amount') || '');
    setWallet(decodeURIComponent(params.get('wallet') || ''));

    // Notify original dashboard tab via BroadcastChannel (same-origin only)
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('aureus_withdrawal_verification');
        channel.postMessage({
          type: isSuccess ? 'withdrawal_verified' : 'withdrawal_verification_failed',
          success: isSuccess
        });
        channel.close();
      }
    } catch {
      // BroadcastChannel not supported — polling fallback handles this
    }
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#111827',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: '#1F2937',
        borderRadius: '16px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        {success === null ? (
          <div style={{ color: '#9CA3AF' }}>Loading...</div>
        ) : success ? (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h1 style={{ color: '#10B981', fontSize: '28px', marginBottom: '16px' }}>
              Withdrawal Verified!
            </h1>
            <p style={{ color: '#D1D5DB', marginBottom: '24px' }}>
              Your withdrawal request has been successfully verified and is now pending admin approval.
            </p>
            {amount && (
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ color: '#9CA3AF', fontSize: '14px', marginBottom: '8px' }}>Amount</div>
                <div style={{ color: '#D4AF37', fontSize: '24px', fontWeight: 'bold' }}>
                  ${parseFloat(amount).toFixed(2)} USDT
                </div>
              </div>
            )}
            {wallet && (
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ color: '#9CA3AF', fontSize: '14px', marginBottom: '8px' }}>Wallet</div>
                <div style={{ color: '#F3F4F6', fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {wallet}
                </div>
              </div>
            )}
            <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
              You will receive an email notification once your withdrawal is processed.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
            <h1 style={{ color: '#EF4444', fontSize: '28px', marginBottom: '16px' }}>
              Verification Failed
            </h1>
            <p style={{ color: '#D1D5DB', marginBottom: '24px' }}>
              {error || 'An error occurred during verification.'}
            </p>
            <div style={{
              backgroundColor: '#7F1D1D20',
              border: '1px solid #DC2626',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#FCA5A5', fontSize: '14px', margin: 0 }}>
                If your verification link has expired, please request a new withdrawal from your dashboard.
              </p>
            </div>
          </>
        )}

        <a
          href="/"
          style={{
            display: 'inline-block',
            backgroundColor: '#D4AF37',
            color: '#1a1a2e',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold',
            marginTop: '16px'
          }}
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
};

export default WithdrawalVerifiedPage;

